import { ClubBookingApiClient } from "../../common/api/ClubBookingApiClient";
import {
  SendStaffMessageRequest,
  StaffMessage,
} from "../../model/members/StaffMessage";

// Staff messaging — backs the Handover panel in the app header.
//
// This used to also carry the staff-task read/create/complete calls for "My Dashboard".
// That page is gone (the feedback removed it) and task creation goes through
// RotaService.createTaskAllocation from Work Allocation, so those methods went with it.
// The /dashboard/tasks endpoints stay live and are covered by the API's own tests.
function normalizeMessageFromApi(raw: Record<string, unknown>): StaffMessage {
  return {
    id: Number(raw.id ?? 0),
    fromStaffId: Number(raw.fromStaffId ?? 0),
    fromStaffName: String(raw.fromStaffName ?? ""),
    toStaffId: Number(raw.toStaffId ?? 0),
    toStaffName: String(raw.toStaffName ?? ""),
    body: String(raw.body ?? ""),
    isRead: Boolean(raw.isRead),
    createdAt: String(raw.createdAt ?? ""),
    readAt: raw.readAt == null ? null : String(raw.readAt),
  };
}

export class RetentionDashboardService extends ClubBookingApiClient {
  /** Messages addressed to the logged-in staff member (most recent first, server-capped). */
  async getMyMessages(): Promise<StaffMessage[]> {
    const raw = await this.get<Record<string, unknown>[]>("/dashboard/messages");
    return Array.isArray(raw) ? raw.map(normalizeMessageFromApi) : [];
  }

  async sendMessage(request: SendStaffMessageRequest): Promise<StaffMessage> {
    const raw = await this.post<Record<string, unknown>>("/dashboard/messages", request, {
      successMessage: "Message sent",
    });
    return normalizeMessageFromApi(raw);
  }

  /**
   * FORT-369: send to every reception staff member individually. Returns the messages the
   * server actually created — one per recipient — so the caller can report how many people
   * were told. A club with no other reception staff is a 400, not an empty success.
   */
  async broadcastToAllStaff(body: string): Promise<StaffMessage[]> {
    // Its own endpoint, so the payload carries no recipient at all — the server resolves
    // who "all reception staff" means. showSuccess is off because the caller reports the
    // recipient count, which a generic "Success!" cannot.
    const raw = await this.post<Record<string, unknown>[]>(
      "/dashboard/messages/broadcast",
      { body },
      { showSuccess: false }
    );
    return Array.isArray(raw) ? raw.map(normalizeMessageFromApi) : [];
  }

  async markMessageRead(messageId: number): Promise<StaffMessage> {
    // Marking read is incidental to reading the panel — a success toast for it is noise.
    const raw = await this.put<Record<string, unknown>>(
      `/dashboard/messages/${messageId}/read`,
      {},
      { showSuccess: false }
    );
    return normalizeMessageFromApi(raw);
  }
}
